// frontend/src/pages/IngredientEdit.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import { fetchWithCSRF } from '../utils/csrfUtils';

function IngredientEdit() {
  const { ingredientId } = useParams();
  const navigate = useNavigate();
  const [ingredient, setIngredient] = useState(null);
  const [unitOptions, setUnitOptions] = useState([]);
  const [foodGroups, setFoodGroups] = useState([]);
  const [selectedFoodGroups, setSelectedFoodGroups] = useState([]);
  const [initialFoodGroups, setInitialFoodGroups] = useState([]);
  const [sessionToken, setSessionToken] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // First useEffect to get the session token
  useEffect(() => {
    const getSessionToken = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!session) {
          throw new Error('No session found');
        }
        if (session?.access_token) {
          setSessionToken(session.access_token);
        } else {
          throw new Error('No access token found in session');
        }
      } catch (err) {
        console.error('Auth error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    }
    getSessionToken();
  }, []);

  // Second useEffect for API calls that need the session token
  useEffect(() => {
    if (!sessionToken || !ingredientId) return;

    console.log('sessionToken is: ', sessionToken + " about to check user");

    //check if user is admin
    fetchWithCSRF('/api/v1/users/check_user', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionToken}`
      }
    })
      .then(response => response.json())
      .then(data => {
        console.log('checkUserdata: ', data);
        if (!data.isAdmin) {
          navigate(`/ingredients/${ingredientId}`);
        } else {
          console.log('User is admin');
        }
      })
      .catch(error => {
        console.error('Error checking user:', error);
        navigate(`/ingredients/${ingredientId}`);
      });

    // Fetch ingredient details
    fetchWithCSRF(`/api/v1/ingredient/${ingredientId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionToken}`
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.length > 0) {
          setIngredient(data[0]);
        }
      })
      .catch(error => console.error('Error fetching ingredient details:', error));

    // Fetch unit of measurement options
    fetchWithCSRF('/api/v1/ingredient/unit_of_measurement_enum', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionToken}`
      }
    })
    .then(response => response.json())
    .then(data => {
      // Add validation to ensure data is in the expected format
      if (Array.isArray(data) && data.length > 0 && data[0].unit) {
        const units = data.map(item => item.unit);
        setUnitOptions(units);
      } else {
        console.error('Unexpected data format for unit options:', data);
      }
    })
    .catch(error => console.error('Error fetching unit options:', error));

  }, [sessionToken, ingredientId]);

  // Third useEffect for API calls - they do need the session token
  useEffect(() => {
    if (!ingredientId || !sessionToken) {
      return;
    }

    setIsLoading(true);
    setError(null);

    // Fetch all available food groups
    const fetchFoodGroups = async () => {
      try {
        const response = await fetchWithCSRF('/api/v1/food-group', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error('Food groups data is not an array');
        }
        
        setFoodGroups(data);
        
        // Now fetch food groups associated with the ingredient
        const ingredientGroupsResponse = await fetchWithCSRF(
          `/api/v1/ingredient-food-group/ingredient_id/${ingredientId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          }
        );

        if (!ingredientGroupsResponse.ok) {
          throw new Error(`HTTP error! status: ${ingredientGroupsResponse.status}`);
        }

        const groupsData = await ingredientGroupsResponse.json();
        if (Array.isArray(groupsData)) {
          const groupIds = groupsData.map(item => item.food_group_id);
          setSelectedFoodGroups(groupIds);
          setInitialFoodGroups(groupIds);
        }
      } catch (err) {
        console.error('Error fetching food groups:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFoodGroups();

  }, [ingredientId, sessionToken]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setIngredient({
      ...ingredient,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFoodGroupChange = (e) => {
    const groupId = parseInt(e.target.value, 10);
    if (e.target.checked) {
      setSelectedFoodGroups([...selectedFoodGroups, groupId]);
    } else {
      setSelectedFoodGroups(selectedFoodGroups.filter(id => id !== groupId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Update the ingredient details
      const response = await fetchWithCSRF(`/api/v1/ingredient/${ingredientId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(ingredient),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update ingredient: ${response.status} ${response.statusText}\n${errorText}`);
      }

      const responseData = await response.json();

      // Determine which food groups to add and which to delete
      const groupsToAdd = selectedFoodGroups.filter(id => !initialFoodGroups.includes(id));
      const groupsToDelete = initialFoodGroups.filter(id => !selectedFoodGroups.includes(id));

      // Create promises for adding new associations
      const addPromises = groupsToAdd.map(groupId =>
        fetchWithCSRF('/api/v1/ingredient-food-group/', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${sessionToken}` 
          },
          body: JSON.stringify({ ingredient_id: ingredientId, food_group_id: groupId }),
        }).then(async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to add food group ${groupId}: ${response.status} ${response.statusText}\n${errorText}`);
          }
          return response.json();
        })
      );

      // Create promises for deleting associations
      const deletePromises = groupsToDelete.map(groupId =>
        fetchWithCSRF(`/api/v1/ingredient-food-group/${ingredientId}/${groupId}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
        }).then(async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to delete food group ${groupId}: ${response.status} ${response.statusText}\n${errorText}`);
          }
          return response;
        })
      );

      // Wait for all add and delete operations to complete
      await Promise.all([...addPromises, ...deletePromises]);
      
      setInitialFoodGroups([...selectedFoodGroups]);
      navigate(`/ingredients/${ingredientId}`);
      
    } catch (error) {
      console.error('Error updating ingredient:', error);
      setError(error.message);
      // Show error in UI instead of alert
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="error-message">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (isLoading || !ingredient || !Array.isArray(foodGroups)) {
    return <div>Loading...</div>;
  }

  return (
    <div className="ingredient-edit">
      <h1>Edit Ingredient</h1>
      <form onSubmit={handleSubmit}>
        {/* Name */}
        <div>
          <label>
            Name:
            <input
              type="text"
              name="name"
              value={ingredient.name || ''}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>
        {/* Description */}
        <div>
          <label>
            Description:
            <textarea
              name="description"
              value={ingredient.description || ''}
              onChange={handleInputChange}
            />
          </label>
        </div>
        {/* Unit of Measurement */}
        <div>
            <label>
                Unit of Measurement:
                <select
                name="unit_of_measurement"
                value={ingredient.unit_of_measurement || ''}
                onChange={handleInputChange}
                >
                <option value="">Select a unit</option>
                {unitOptions.map(option => (
                    <option key={option} value={option}>
                    {option}
                    </option>
                ))}
                </select>
            </label>
        </div>
        {/* Estimated Shelf Life */}
        <div>
          <label>
            Estimated Shelf Life:
            <input
              type="number"
              name="estimated_shelf_life"
              value={ingredient.estimated_shelf_life || ''}
              onChange={handleInputChange}
            />
          </label>
        </div>
        {/* Serving Size */}
        <div>
          <label>
            Serving Size:
            <input
              type="number"
              name="serving_size"
              value={ingredient.serving_size || ''}
              onChange={handleInputChange}
            />
          </label>
        </div>
        {/* Macronutrients */}
        <div>
          <label>
            Protein:
            <input
              type="number"
              name="protein"
              value={ingredient.protein || ''}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Carbohydrate:
            <input
              type="number"
              name="carbohydrate"
              value={ingredient.carbohydrate || ''}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Fat:
            <input
              type="number"
              name="fat"
              value={ingredient.fat || ''}
              onChange={handleInputChange}
            />
          </label>
        </div>
        {/* Micronutrients */}
        <div>
          <label>
            Micronutrients (JSON format):
            <textarea
              name="micronutrients"
              value={ingredient.micronutrients || ''}
              onChange={handleInputChange}
            />
          </label>
        </div>
        {/* Estimated Cost Per Unit */}
        <div>
          <label>
            Estimated Cost Per Unit (Cents):
            <input
              type="number"
              name="estimated_cost_per_unit_cents"
              value={ingredient.estimated_cost_per_unit_cents || ''}
              onChange={handleInputChange}
            />
          </label>
        </div>
        {/* Flags */}
        <div>
          <label>
            Is Perishable:
            <input
              type="checkbox"
              name="is_perishable"
              checked={ingredient.is_perishable || false}
              onChange={handleInputChange}
            />
          </label>
        </div>
        <div>
          <label>
            Is Vegetarian:
            <input
              type="checkbox"
              name="is_vegetarian"
              checked={ingredient.is_vegetarian || false}
              onChange={handleInputChange}
            />
          </label>
        </div>
        <div>
          <label>
            Is Vegan:
            <input
              type="checkbox"
              name="is_vegan"
              checked={ingredient.is_vegan || false}
              onChange={handleInputChange}
            />
          </label>
        </div>
        {/* Food Groups */}
        <div>
          <label>Food Groups:</label>
          {foodGroups.map(group => (
            <div key={group.id}>
              <input
                type="checkbox"
                value={group.id}
                checked={selectedFoodGroups.includes(group.id)}
                onChange={handleFoodGroupChange}
              />
              {group.name}
            </div>
          ))}
        </div>
        {/* Submit Button */}
        <button type="submit">Update Ingredient</button>
      </form>
    </div>
  );
}

export default IngredientEdit;
